import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, CheckCircle2, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { usePendingPremiumRequest } from "@/hooks/usePendingPremiumRequest";
import { getPackageLabel } from "@/lib/planFeatures";
import { PENDING_REQUEST_COPY } from "@/lib/pendingRequestCopy";

interface UpgradeFlowContextValue {
  requestUpgrade: (target?: string) => void;
  hasPendingRequest: boolean;
  /**
   * True while the pending-request lookup is in flight or the dialog is being
   * opened. CTA buttons should disable themselves and show a spinner so we get
   * a consistent loading state across every page that calls requestUpgrade().
   */
  isUpgradeBusy: boolean;
}

const Ctx = createContext<UpgradeFlowContextValue | null>(null);

export const useUpgradeFlow = () => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useUpgradeFlow must be used within UpgradeFlowProvider");
  return ctx;
};

export const UpgradeFlowProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  // Poll every 8s while dialog is open so approval is detected in near real-time.
  const { data: pending, isFetching, isLoading: isPendingLoading } = usePendingPremiumRequest({
    refetchInterval: open ? 8000 : false,
  });
  const [justApproved, setJustApproved] = useState(false);
  const wasPendingRef = useRef(false);
  // Tracks which pending request id has already triggered the dialog in this
  // session. Subsequent requestUpgrade() calls while the same pending request
  // exists become no-ops, so users cannot re-open the popup repeatedly.
  const shownForPendingIdRef = useRef<string | null>(null);

  // Reset the one-time gate whenever the pending request disappears (approved,
  // rejected, or canceled) so a future pending request can prompt again.
  useEffect(() => {
    if (!pending) {
      shownForPendingIdRef.current = null;
    }
  }, [pending]);

  // Detect pending -> approved transition while dialog is open.
  useEffect(() => {
    if (pending) {
      wasPendingRef.current = true;
      return;
    }
    if (open && wasPendingRef.current) {
      wasPendingRef.current = false;
      setJustApproved(true);
      queryClient.invalidateQueries({ queryKey: ["active-subscriptions"] });
      queryClient.invalidateQueries({ queryKey: ["subscription-status"] });
      queryClient.invalidateQueries({ queryKey: ["user-programs"] });
      toast.success(
        t("pendingRequest.approvedTitle", "Aboneliğin onaylandı"),
        {
          description: t(
            "pendingRequest.approvedDesc",
            "Premium içeriklere artık erişebilirsin.",
          ),
        },
      );
    }
  }, [pending, open, queryClient, t]);

  const requestUpgrade = useCallback(
    (target = "/premium") => {
      // Prevent re-entry while we are already opening or refetching pending.
      if (isOpening) return;
      if (pending) {
        if (shownForPendingIdRef.current === pending.id) return;
        if (open) return;
        setIsOpening(true);
        shownForPendingIdRef.current = pending.id;
        setJustApproved(false);
        setOpen(true);
        // Release the busy lock on the next tick once the dialog mounts.
        setTimeout(() => setIsOpening(false), 0);
        return;
      }
      setIsOpening(true);
      navigate(target);
      setTimeout(() => setIsOpening(false), 0);
    },
    [pending, open, isOpening, navigate],
  );

  const isUpgradeBusy = isOpening || isPendingLoading;

  const value = useMemo<UpgradeFlowContextValue>(
    () => ({ requestUpgrade, hasPendingRequest: !!pending, isUpgradeBusy }),
    [requestUpgrade, pending, isUpgradeBusy],
  );

  const planLabel = pending ? getPackageLabel(pending.plan_name as any) || pending.plan_name : "";
  const locale = i18n.language === "tr" ? "tr-TR" : "en-US";
  const createdAt = pending
    ? new Date(pending.created_at).toLocaleDateString(locale, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "";

  return (
    <Ctx.Provider value={value}>
      {children}
      <Dialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setJustApproved(false);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div
              className={`mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-3 ${
                justApproved ? "bg-emerald-500/15" : "bg-amber-500/15"
              }`}
            >
              {justApproved ? (
                <CheckCircle2 className="w-7 h-7 text-emerald-500" />
              ) : (
                <Clock className="w-7 h-7 text-amber-500" />
              )}
            </div>
            <DialogTitle className="text-center">
              {justApproved
                ? t("pendingRequest.approvedTitle", "Aboneliğin onaylandı")
                : t(PENDING_REQUEST_COPY.titleKey, PENDING_REQUEST_COPY.titleFallback)}
            </DialogTitle>
            <DialogDescription className="text-center">
              {justApproved
                ? t(
                    "pendingRequest.approvedDesc",
                    "Premium içeriklere artık erişebilirsin.",
                  )
                : t(PENDING_REQUEST_COPY.descKey, PENDING_REQUEST_COPY.descFallback)}
            </DialogDescription>
          </DialogHeader>

          {pending && !justApproved && (
            <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t("pendingRequest.plan", "Paket")}</span>
                <span className="font-medium text-foreground">{planLabel}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t("pendingRequest.amount", "Tutar")}</span>
                <span className="font-medium text-foreground">₺{pending.amount}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t("pendingRequest.date", "Talep tarihi")}</span>
                <span className="font-medium text-foreground">{createdAt}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t("pendingRequest.status", "Durum")}</span>
                <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30">
                  {t("premium.pending", "Beklemede")}
                </Badge>
              </div>
              <div className="flex items-center gap-2 pt-2 text-[11px] text-muted-foreground">
                <Loader2 className={`w-3 h-3 ${isFetching ? "animate-spin" : "opacity-40"}`} />
                <span>
                  {t(
                    "pendingRequest.autoCheck",
                    "Onay durumu otomatik olarak kontrol ediliyor…",
                  )}
                </span>
              </div>
            </div>
          )}

          <DialogFooter className="sm:flex-row gap-2">
            {justApproved ? (
              <Button
                variant="hero"
                className="flex-1"
                onClick={() => {
                  setOpen(false);
                  setJustApproved(false);
                  navigate("/premium#current-plan");
                }}
              >
                {t("pendingRequest.goPremium", "Premium içeriklere git")}
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setOpen(false)}
                >
                  {t("pendingRequest.close", "Kapat")}
                </Button>
                <Button
                  variant="hero"
                  className="flex-1"
                  onClick={() => {
                    setOpen(false);
                    navigate("/premium/requests");
                  }}
                >
                  {t("pendingRequest.viewRequest", "Talebimi Gör")}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Ctx.Provider>
  );
};
